import { CRAYO_ENTRY, formatHours, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;

export const QUSO_CONTENT: CompareContent = {
  slug: "crayo-vs-quso",
  competitorSlug: "quso",
  title: "Crayo vs Quso.ai (formerly Vidyo.ai)",
  seoTitle: "Crayo vs Quso.ai (formerly Vidyo.ai): Pricing and Features",
  seoDescription:
    "Crayo vs Quso.ai, formerly Vidyo.ai, with dated prices. Quso charges 1 credit per processed minute and has no API. Crayo charges 1 credit per finished clip and puts an API and a timeline editor on every plan.",
  cardVerdict: "Better than Crayo for free tier and scheduling, behind on API and metering.",
  bestFor: "Vidyo.ai clippers weighing a switch",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Crayo is the better pick if you clip long podcasts and streams and want to pay per finished clip rather than per processed minute, with a public API and a timeline editor on one ${money(hobby.monthly)} plan. Quso.ai, formerly Vidyo.ai, is the better pick if you need a free tier, a scheduler to seven platforms, or post analytics. The evidence is below.`,
  pickCrayoIf: [
    "You pay per finished clip: a 60-minute source that yields 8 clips costs 8 credits on Crayo and 60 credits on Quso.ai.",
    "You want a public API: Quso.ai's FAQ answers Not at the moment, and Crayo has 34 endpoints on every plan.",
    "You clip short sources too: Quso.ai generates no clips for videos under 3 minutes, while Crayo AutoClip takes sources from 1 minute to 3 hours.",
  ],
  pickThemIf: [
    "You need a free tier: Quso.ai gives 75 credits a month at 720p with a watermark. Crayo has no free plan.",
    "You schedule posts from the clipping tool: Essential publishes to 7 platforms. Crayo has no scheduler.",
    "You bill yearly and process many minutes: annual billing doubles the credits, so Lite yearly is 200 minutes for $19 a month.",
  ],
  wins: [
    {
      claim: "Crayo meters by finished clip; Quso.ai meters by processed minute, rounded up.",
      evidence:
        "Quso.ai's general FAQ, checked Oct 8, 2026, defines one credit as one minute of processed video rounded up to the next minute, so a 60-minute podcast costs 60 credits however many clips you keep. Crayo charges 1 workflow credit per clip you generate.",
      href: "https://quso.ai/help/general-faq",
    },
    {
      claim: "Crayo has a public API on every plan; Quso.ai has none.",
      evidence:
        "Asked whether an API is available, the Quso.ai FAQ answers Not at the moment, as of Oct 8, 2026. Crayo's API has 34 endpoints under /v1, including autoclip, exports, transcripts and webhooks, with one key per account and docs at crayo.ai/developers.",
      href: "https://crayo.ai/developers",
    },
    {
      claim: "Crayo's editor is a timeline with a camera track and keyframes; Quso.ai describes editing like a doc.",
      evidence:
        "Quso.ai's homepage says Edit video like a doc, and its advanced editor is listed on Lite and up as of Oct 8, 2026. Crayo's timeline editor, with camera track, keyframes, zooms, speed ramps and 32 caption styles, is on every plan.",
      href: "https://quso.ai/",
    },
    {
      claim: "Crayo clips sources from 1 minute long; Quso.ai does not generate clips for videos under 3 minutes.",
      evidence:
        "The Quso.ai FAQ states that clips are not generated for videos under 3 minutes or without speech, checked Oct 8, 2026. Crayo AutoClip accepts sources from 1 minute to 3 hours and up to 4 GB, by YouTube or TikTok link or file upload, and returns 1 to 20 clips of 15 to 120 seconds per run.",
      href: "https://quso.ai/help/general-faq",
    },
  ],
  concessions: [
    {
      claim: "Quso.ai has a real free tier.",
      evidence:
        "75 credits a month at $0, rendered at 720p with a watermark and 7 days of data retention, per the pricing page checked Oct 8, 2026. That is enough to clip one hour-long source a month before paying. Crayo has no free plan and no trial.",
      href: "https://quso.ai/pricing",
    },
    {
      claim: "Quso.ai schedules posts to 7 platforms and reports post analytics.",
      evidence:
        "Essential at $39 a month includes Schedule to 7 platforms and a Content Planner; Growth at $49 adds social analytics and a brand kit, per the pricing page checked Oct 8, 2026. Crayo exports the file and you post it yourself.",
      href: "https://quso.ai/pricing",
    },
    {
      claim: "Quso.ai doubles your credits on annual billing.",
      evidence:
        "Lite goes from 100 to 200 credits, Essential from 300 to 600, and Growth from 600 to 1,200 when billed yearly, at $19, $26 and $33 a month. Crayo's credit counts are the same on monthly and yearly billing.",
      href: "https://quso.ai/pricing",
    },
    {
      claim: "Quso.ai captions more languages than Crayo transcribes.",
      evidence:
        "Quso.ai's homepage says 50+ languages in one card and 100+ languages in its FAQ, checked Oct 8, 2026; either figure is above Crayo's 56 spoken languages.",
      href: "https://quso.ai/",
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes`,
      note: "Prices from the live plan table.",
    },
    them: {
      plan: "Essential",
      cost: "$39/mo or $26/mo yearly",
      usage:
        "240 of 300 credits, or 240 of 600 on yearly; Lite's 100 monthly credits run out in the second week and its 200 yearly credits in the fourth",
      note: "1 credit per processed minute, rounded up, per the Quso.ai FAQ.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 45-minute two-person podcast recording owned by Crayo",
    plans: "Quso.ai Lite and Crayo Clipper, same file, same day, defaults first then one tuned run each",
  },
  billingNote:
    "Quso.ai monthly credits expire at the end of each month, add-on credits never expire, and annual billing doubles the allowance. Both monthly and annual plans are non-refundable, per the general FAQ, and failed videos are refunded in credits.",
  faqs: [
    {
      question: "Is Crayo cheaper than Quso.ai?",
      answer:
        `For most clippers, yes, on what a clip costs rather than on the sticker. Crayo Hobby is ${crayoPlanTerms(hobby)} for ${hobby.credits} credits, and one credit is one finished clip. Quso.ai Lite is $29 a month, or $19 a month billed yearly, for 100 credits, so the two entry prices match on monthly billing and Crayo's quarterly price matches Quso.ai's yearly one, with Crayo yearly the lowest of the set. One Quso.ai credit is one processed minute rounded up, so two hour-long sources use the whole allowance before any clip is counted. Annual Lite doubles that to 200 credits, and Essential is $39 a month, or $26 billed yearly, for 300 or 600. Crayo Hobby also includes ${formatHours(hobby.exportMinutes)} of export, ${hobby.voiceoverMinutes} voiceover minutes and ${hobby.images} AI images. If you process short sources and want a free tier to start on, Quso.ai can cost less.`,
    },
    {
      question: "Does Crayo have a free plan like Quso.ai?",
      answer:
        `No. Crayo has no free plan and no trial; the only free products are twelve browser tools at crayo.ai/features/free that need no account and never upload the file. The entry plan is Hobby at ${money(hobby.monthly)} a month, or ${money(hobby.yearlyTotal)} a year, and every tool is on it. Quso.ai's free plan gives 75 credits a month, rendered at 720p with a watermark, accepts uploads up to 5 GB, publishes to TikTok, and keeps your data for 7 days. To get 1080p clips without the watermark you move to Lite at $29 a month or $19 billed yearly.`,
    },
    {
      question: "Is Quso.ai the same as Vidyo.ai?",
      answer:
        "Yes. Vidyo.ai rebranded to Quso.ai in January 2025, and the company explains the change at quso.ai/vidyo-ai. The legal entity is still Vidyoai Internet Ventures, Inc., founded in December 2021 and based in New York, and the homepage claims 4M+ creators and businesses. The clipping product, the credit system and the pricing page all carry the Quso.ai name now, so a search for a vidyo ai alternative or vidyo.ai pricing lands on the same plans compared here: Free, Lite, Essential and Growth. This page uses the current name and treats Vidyo.ai as the same product.",
    },
    {
      question: "Does Crayo auto-post like Quso.ai?",
      answer:
        "No. Crayo exports the file and you post it yourself. Quso.ai Essential, at $39 a month, schedules to 7 platforms and includes a Content Planner, and the free plan publishes to TikTok. If native scheduling is the deciding feature, Quso.ai wins that row, and Growth at $49 adds social analytics on top. Crayo's View Tracker follows Instagram, YouTube and TikTok creators for posting cadence and viral outliers, 100 tracker credits a month on Hobby and 2,500 on Pro, but it is not a scheduler and not per-post analytics.",
    },
    {
      question: "Does Quso.ai have an API?",
      answer:
        "No. The Quso.ai general FAQ answers the API question with Not at the moment, as of Oct 8, 2026. Crayo has a public API on every plan with 34 endpoints under /v1, covering autoclip, exports, projects, uploads, transcripts, voiceover, images and webhooks with a test endpoint. You get one key per account and the docs are at crayo.ai/developers. The API is on Hobby as well as Clipper and Pro, so a workflow that submits a source, polls the job and receives the export link by webhook works at the entry price.",
    },
    {
      question: "What happens to unused Quso.ai credits?",
      answer:
        "On monthly plans, credits renew each month and unused credits expire, per the Quso.ai FAQ. Add-on credits bought separately never expire, and a failed video is refunded in credits automatically. Two FAQ rules also decide how many credits a source costs: a minute is rounded up, so a 61-minute file is 62 credits, and no clips are generated for videos under 3 minutes or without speech. Crayo works the same way on the base allowance: workflow credits reset each cycle with no rollover, and top-up packs are separate. The difference is the unit, since Crayo counts the finished clip, not the minutes it read.",
    },
    {
      question: "Can I get a refund from Quso.ai or Crayo?",
      answer:
        "Neither. Quso.ai's FAQ states Monthly: Non-refundable and Annual: Non-refundable. Crayo's refund policy is that all sales are final. On Crayo you can cancel any time in one click from Settings, the plan stays active to the end of the cycle, and nothing further is charged. Quso.ai's FAQ and pricing page do not describe how to cancel, so check your account settings there before the renewal date. On both products the safe move is a monthly plan for the first month, since an annual charge is final on either side.",
    },
    {
      question: "How does Quso.ai compare with Opus Clip?",
      answer:
        `Both meter by source minute: one Quso.ai credit is one processed minute rounded up, and one Opus Clip credit is one uploaded minute. Quso.ai Lite is $29 a month for 100 credits; Opus Clip Starter is $15 a month for 150 credits, monthly only. Both have free tiers and schedulers, and Quso.ai's own Opus Clip comparison page concedes that quso.ai isn't the cheapest clipper. Crayo differs from both by charging per finished clip, ${hobby.credits} on Hobby, and by putting a public API and a timeline editor on every plan. See the Crayo vs Opus Clip page for the full table.`,
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. Quso.ai prices checked Oct 8, 2026: Lite $29/mo or $19/mo billed yearly, Essential $39/mo or $26/mo billed yearly, 1 credit per processed minute, no API.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-opus-clip", "crayo-vs-vizard", "crayo-vs-klap"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "vidyo ai alternative",
    "quso ai alternative",
    "quso vs opus",
    "vidyo.ai pricing",
    "quso ai pricing",
    "crayo vs quso",
  ],
};
